import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API, getToken } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { fmtDate, inr, isoDateInput } from "../../lib/format";
import PortfolioOverview from "../../components/PortfolioOverview";

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [overview, setOverview] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [comment, setComment] = useState("");
  const [pay, setPay] = useState({
    bank_name: "", payment_date: isoDateInput(), account_number: "", reference_utr: "", amount: 0, notes: "",
  });
  const [port, setPort] = useState({
    strategy_id: "",
    start_date: isoDateInput(),
    end_date: isoDateInput(new Date(Date.now() + 365 * 86400000)),
    is_complimentary: false,
    payment_id: "",
  });

  const isAdmin = user?.role === "admin";

  const load = () => {
    api.get(`/admin/clients/${id}`).then((r) => setClient(r.data)).catch(() => {});
    api.get("/strategies").then((r) => setStrategies(r.data)).catch(() => {});
  };
  useEffect(load, [id]);

  const [selectedPid, setSelectedPid] = useState(null);
  useEffect(() => {
    if (!selectedPid) { setOverview(null); return; }
    api.get(`/admin/clients/${id}/portfolios/${selectedPid}/overview`).then((r) => setOverview(r.data)).catch(() => setOverview({ error: true }));
  }, [id, selectedPid]);

  // Subscription extension modal state (declared before any early-return to keep hook order stable)
  const [extendPort, setExtendPort] = useState(null);
  const [extendForm, setExtendForm] = useState({ months: 12, is_complimentary: false, payment_reference: "", amount: "", notes: "" });
  const [extendBusy, setExtendBusy] = useState(false);

  const resetClientPassword = async () => {
    const np = window.prompt(`Set a new password for ${client?.email} (≥ 8 chars):`, "");
    if (!np) return;
    try {
      await api.post(`/admin/users/${id}/reset-password`, { new_password: np });
      toast.success("Password reset. Share the new password with the customer.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (!client) return <div className="text-zinc-500">Loading...</div>;

  const addComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      await api.post(`/admin/clients/${id}/comments`, { body: comment });
      setComment("");
      toast.success("Comment added");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const addPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/clients/${id}/payments`, { ...pay, amount: Number(pay.amount) });
      toast.success("Payment recorded");
      setPay({ bank_name: "", payment_date: isoDateInput(), account_number: "", reference_utr: "", amount: 0, notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const allotPortfolio = async (e) => {
    e.preventDefault();
    if (!port.strategy_id) { toast.error("Pick a strategy"); return; }
    try {
      await api.post(`/admin/clients/${id}/portfolios`, port);
      toast.success("Portfolio allotted");
      setPort({ ...port, strategy_id: "", payment_id: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const togglePortfolio = async (pid) => {
    try {
      await api.post(`/admin/portfolios/${pid}/toggle-status`);
      toast.success("Portfolio status toggled");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const deletePortfolio = async (p) => {
    const sname = strategies.find((s) => s.strategy_id === p.strategy_id)?.name || "—";
    const ok = window.confirm(`Delete the '${sname}' portfolio for this client?\n\nThis is permanent and will also remove all holdings + chart snapshots for this subscription.`);
    if (!ok) return;
    try {
      const r = await api.delete(`/admin/portfolios/${p.portfolio_id}`);
      toast.success(`Deleted '${sname}' (${r.data.deleted.holdings} holdings, ${r.data.deleted.snapshots} snapshots cleaned)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  // Subscription extension helpers
  const openExtend = (p) => {
    setExtendPort(p);
    setExtendForm({ months: 12, is_complimentary: false, payment_reference: "", amount: "", notes: "" });
  };
  const submitExtend = async (e) => {
    e.preventDefault();
    if (!extendPort) return;
    if (!extendForm.is_complimentary && !extendForm.payment_reference.trim()) {
      toast.error("Payment reference is required for paid extensions");
      return;
    }
    setExtendBusy(true);
    try {
      const body = {
        months: Number(extendForm.months),
        is_complimentary: extendForm.is_complimentary,
        payment_reference: extendForm.payment_reference.trim() || null,
        amount: extendForm.amount === "" ? null : Number(extendForm.amount),
        notes: extendForm.notes.trim() || null,
      };
      const r = await api.post(`/admin/clients/${id}/portfolios/${extendPort.portfolio_id}/extend`, body);
      toast.success(`Extended by ${extendForm.months} months → ${r.data.new_end_date}`);
      setExtendPort(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Extend failed");
    } finally {
      setExtendBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Client</div>
          <h1 className="font-display font-bold text-3xl text-zinc-950 mt-1">{client.name}</h1>
          <div className="flex gap-3 items-center mt-2 text-sm text-zinc-500">
            <span className="font-mono">{client.email}</span>
            {client.email_verified ? (
              <Badge className="bg-green-50 text-green-700 border-green-200">verified</Badge>
            ) : (
              <Badge className="bg-amber-50 text-amber-700 border-amber-200">pending</Badge>
            )}
            <span className="font-mono">{client.mobile}</span>
          </div>
        </div>
        {isAdmin && (
          <Button data-testid="reset-client-pwd" variant="outline" size="sm" onClick={resetClientPassword} className="text-xs">Reset password</Button>
        )}
      </div>

      <Tabs defaultValue="portfolio">
        <TabsList className="bg-zinc-100">
          <TabsTrigger value="portfolio" data-testid="tab-portfolio">Portfolio</TabsTrigger>
          {isAdmin && <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>}
          {isAdmin && <TabsTrigger value="allot" data-testid="tab-allot">Allot Portfolio</TabsTrigger>}
          <TabsTrigger value="comments" data-testid="tab-comments">Comments</TabsTrigger>
          {isAdmin && <TabsTrigger value="notify" data-testid="tab-notify">Notifications</TabsTrigger>}
        </TabsList>

        {isAdmin && (
          <TabsContent value="notify" className="mt-4">
            <NotifyPanel client={client} onClientUpdate={(patch) => setClient({ ...client, ...patch })} />
          </TabsContent>
        )}

        <TabsContent value="portfolio" className="mt-4 space-y-4">
          {!selectedPid && (
            <>
              <div className="flex items-center justify-between">
                <div className="text-sm text-zinc-600">
                  {(client.portfolios || []).length === 0
                    ? "No portfolios assigned yet."
                    : `${client.portfolios.length} portfolio subscription${client.portfolios.length > 1 ? "s" : ""}. Click any card to view KPIs, holdings, and chart.`}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(client.portfolios || []).map((p) => {
                  const sname = strategies.find((s) => s.strategy_id === p.strategy_id)?.name || "—";
                  const iv = p.initial_value;
                  const cv = p.current_value || 0;
                  // Prefer server-computed absolute_returns_pct (uses live LTP path,
                  // matches Overview KPI). Fallback keeps old behaviour for any
                  // legacy payload without the field.
                  const pnl = p.absolute_returns_pct != null
                    ? p.absolute_returns_pct
                    : (iv ? ((cv - iv) / iv) * 100 : null);
                  const pnlClass = pnl == null ? "text-zinc-500" : pnl > 0 ? "text-profit" : pnl < 0 ? "text-loss" : "text-zinc-700";
                  const isActive = p.status === "active";
                  return (
                    <Card key={p.portfolio_id} data-testid={`staff-port-card-${p.portfolio_id}`} onClick={() => setSelectedPid(p.portfolio_id)} className="border-zinc-200 shadow-none cursor-pointer hover:border-zinc-400 transition-colors">
                      <CardContent className="p-5 space-y-3">
                        <Badge className="bg-purple-50 text-purple-700 border-purple-200">{sname}</Badge>
                        <div className="font-display font-bold text-lg text-zinc-950">{sname}</div>
                        <div className="text-[11px] text-zinc-500 font-mono">{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</div>
                        <div className="flex items-center gap-2">
                          <Badge className={isActive ? "bg-green-50 text-green-700 border-green-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"}>{isActive ? "Active" : "Inactive"}</Badge>
                          {p.is_complimentary && <Badge className="bg-amber-50 text-amber-700 border-amber-200">Comp.</Badge>}
                        </div>
                        {!iv ? (
                          <div className="text-xs text-amber-700 pt-2 border-t border-zinc-100">Customer has not defined initial value.</div>
                        ) : (
                          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-100">
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Current</div>
                              <div className="font-mono text-sm mt-0.5">{inr(cv, { decimals: 0 })}</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">P&amp;L</div>
                              <div className={`font-mono text-sm mt-0.5 ${pnlClass}`}>{pnl == null ? "—" : pnl.toFixed(2) + "%"}</div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
          {selectedPid && (() => {
            const selectedP = (client.portfolios || []).find((p) => p.portfolio_id === selectedPid);
            const sName = selectedP ? (strategies.find((s) => s.strategy_id === selectedP.strategy_id)?.name || "—") : "—";
            const totalPorts = (client.portfolios || []).length;
            const portIdx = selectedP ? (client.portfolios || []).findIndex((p) => p.portfolio_id === selectedP.portfolio_id) + 1 : 0;
            const exportUrl = (fmt) => `${API}/admin/clients/${id}/portfolios/${selectedPid}/export/${fmt}`;
            const downloadExport = async (fmt) => {
              try {
                const r = await fetch(exportUrl(fmt), { headers: { Authorization: `Bearer ${getToken()}` } });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const blob = await r.blob();
                const cd = r.headers.get("content-disposition") || "";
                const m = cd.match(/filename="([^"]+)"/);
                const fname = m ? m[1] : `portfolio.${fmt}`;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = fname;
                document.body.appendChild(a); a.click();
                a.remove(); window.URL.revokeObjectURL(url);
                toast.success(`Downloaded ${fmt.toUpperCase()}`);
              } catch (e) {
                toast.error(`Export failed: ${e.message}`);
              }
            };
            return (
              <>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <Button data-testid="back-to-portfolios" size="sm" variant="outline" onClick={() => setSelectedPid(null)} className="text-xs">← Back to all portfolios</Button>
                  {selectedP && (
                    <div className="flex items-center gap-3 flex-wrap" data-testid="portfolio-header">
                      <Badge className="bg-purple-50 text-purple-700 border-purple-200 font-display font-semibold" data-testid="portfolio-strategy-badge">{sName}</Badge>
                      <div className="text-xs text-zinc-500 font-mono">
                        {fmtDate(selectedP.start_date)} → {fmtDate(selectedP.end_date)}
                      </div>
                      {selectedP.is_complimentary && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Complimentary</Badge>}
                      {totalPorts > 1 && (
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                          Portfolio {portIdx} of {totalPorts}
                        </div>
                      )}
                      {isAdmin && overview && !overview.error && !overview.needs_value && (
                        <div className="flex items-center gap-2 ml-2 pl-3 border-l border-zinc-200">
                          <Button data-testid="export-xlsx-btn" size="sm" variant="outline" onClick={() => downloadExport("xlsx")} className="text-xs h-7">
                            Export Excel
                          </Button>
                          <Button data-testid="export-pdf-btn" size="sm" variant="outline" onClick={() => downloadExport("pdf")} className="text-xs h-7">
                            Export PDF
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {!overview && <div className="text-zinc-500">Loading...</div>}
                {overview?.error && <Card><CardContent className="p-6 text-loss">Portfolio not found.</CardContent></Card>}
                {overview?.needs_value && <Card><CardContent className="p-6 text-amber-700">Customer has not yet defined initial portfolio value.</CardContent></Card>}
                {overview && !overview.error && !overview.needs_value && <PortfolioOverview data={overview} />}
              </>
            );
          })()}
        </TabsContent>

        <TabsContent value="payments" className="mt-4 space-y-4">
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-6">
              <h3 className="font-display font-semibold text-zinc-950 mb-4">Add payment</h3>
              <form onSubmit={addPayment} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Bank name</Label><Input required value={pay.bank_name} onChange={(e) => setPay({...pay, bank_name: e.target.value})} className="mt-1" /></div>
                <div><Label>Payment date</Label><Input type="date" required value={pay.payment_date} onChange={(e) => setPay({...pay, payment_date: e.target.value})} className="mt-1" /></div>
                <div><Label>Account number</Label><Input required value={pay.account_number} onChange={(e) => setPay({...pay, account_number: e.target.value})} className="mt-1" /></div>
                <div><Label>UTR / Reference</Label><Input required value={pay.reference_utr} onChange={(e) => setPay({...pay, reference_utr: e.target.value})} className="mt-1" /></div>
                <div><Label>Amount (₹)</Label><Input type="number" required value={pay.amount} onChange={(e) => setPay({...pay, amount: e.target.value})} className="mt-1" /></div>
                <div><Label>Notes</Label><Input value={pay.notes} onChange={(e) => setPay({...pay, notes: e.target.value})} className="mt-1" /></div>
                <div className="md:col-span-2"><Button data-testid="add-payment-btn" type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white">Save payment</Button></div>
              </form>
            </CardContent>
          </Card>
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-0">
              <div className="px-6 py-4 border-b border-zinc-200 font-display font-semibold text-zinc-950">Payment history</div>
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Bank</TableHead><TableHead>Account</TableHead><TableHead>UTR</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(client.payments || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-zinc-500">No payments yet.</TableCell></TableRow>}
                  {(client.payments || []).map((p) => (
                    <TableRow key={p.payment_id}>
                      <TableCell className="font-mono text-sm">{fmtDate(p.payment_date)}</TableCell>
                      <TableCell>{p.bank_name}</TableCell>
                      <TableCell className="font-mono text-sm">{p.account_number}</TableCell>
                      <TableCell className="font-mono text-sm">{p.reference_utr}</TableCell>
                      <TableCell className="text-right font-mono">{inr(p.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="allot" className="mt-4 space-y-4">
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-6">
              <h3 className="font-display font-semibold text-zinc-950 mb-4">Allot portfolio</h3>
              <form onSubmit={allotPortfolio} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Strategy</Label>
                  <Select value={port.strategy_id} onValueChange={(v) => setPort({...port, strategy_id: v})}>
                    <SelectTrigger data-testid="port-strategy-select" className="mt-1"><SelectValue placeholder="Pick a strategy created by RA…" /></SelectTrigger>
                    <SelectContent>
                      {strategies.filter((s) => s.is_active).map((s) => (
                        <SelectItem key={s.strategy_id} value={s.strategy_id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1">Strategies are created by RA in the Research Desk → Strategies tab.</p>
                </div>
                <div><Label>Start date</Label><Input type="date" required value={port.start_date} onChange={(e) => setPort({...port, start_date: e.target.value})} className="mt-1" /></div>
                <div><Label>End date</Label><Input type="date" required value={port.end_date} onChange={(e) => setPort({...port, end_date: e.target.value})} className="mt-1" /></div>
                <div className="flex items-center gap-3">
                  <Switch data-testid="complimentary-switch" checked={port.is_complimentary} onCheckedChange={(v) => setPort({...port, is_complimentary: v})} />
                  <Label>Complimentary (no payment required)</Label>
                </div>
                {!port.is_complimentary && (
                  <div>
                    <Label>Link payment</Label>
                    <Select value={port.payment_id} onValueChange={(v) => setPort({...port, payment_id: v})}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select payment" /></SelectTrigger>
                      <SelectContent>
                        {(client.payments || []).map((p) => (
                          <SelectItem key={p.payment_id} value={p.payment_id}>
                            {fmtDate(p.payment_date)} · {p.bank_name} · {inr(p.amount)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="md:col-span-2"><Button data-testid="allot-portfolio-btn" type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white">Allot portfolio</Button></div>
              </form>
            </CardContent>
          </Card>
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-0">
              <div className="px-6 py-4 border-b border-zinc-200 font-display font-semibold text-zinc-950">Existing portfolios</div>
              <Table>
                <TableHeader><TableRow><TableHead>Strategy</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead><TableHead>Status</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(client.portfolios || []).map((p) => {
                    const sname = strategies.find((s) => s.strategy_id === p.strategy_id)?.name || "—";
                    return (
                    <TableRow key={p.portfolio_id}>
                      <TableCell><Badge className="bg-purple-50 text-purple-700 border-purple-200">{sname}</Badge></TableCell>
                      <TableCell className="font-mono text-sm">{fmtDate(p.start_date)}</TableCell>
                      <TableCell className="font-mono text-sm">{fmtDate(p.end_date)}</TableCell>
                      <TableCell>
                        {p.status === "active" ? (
                          <Badge className="bg-green-50 text-green-700 border-green-200">active</Badge>
                        ) : (
                          <Badge className="bg-zinc-50 text-zinc-600 border-zinc-200">inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell>{p.is_complimentary ? "Complimentary" : "Paid"}</TableCell>
                      <TableCell className="text-right">
                        {isAdmin && (
                          <Button data-testid={`extend-portfolio-${p.portfolio_id}`} size="sm" variant="ghost" onClick={() => openExtend(p)} className="text-xs">
                            Extend
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => togglePortfolio(p.portfolio_id)} className="text-xs">
                          Toggle
                        </Button>
                        <Button data-testid={`delete-portfolio-${p.portfolio_id}`} size="sm" variant="ghost" onClick={() => deletePortfolio(p)} className="text-xs text-loss hover:text-loss hover:bg-red-50">
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="comments" className="mt-4 space-y-4">
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-6">
              <form onSubmit={addComment} className="space-y-3">
                <Label>New comment (visible to Admin & CRM)</Label>
                <Textarea data-testid="comment-input" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="e.g., Called client, payment confirmed..." />
                <Button data-testid="add-comment-btn" type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white">Post comment</Button>
              </form>
            </CardContent>
          </Card>
          <div className="space-y-3">
            {(client.comments || []).length === 0 && <div className="text-zinc-500 text-sm">No comments yet.</div>}
            {(client.comments || []).map((c) => (
              <Card key={c.comment_id} className="border-zinc-200 shadow-none">
                <CardContent className="p-4">
                  <div className="flex justify-between text-xs text-zinc-500 mb-1">
                    <span><span className="font-medium text-zinc-700">{c.author_name}</span> · {c.author_role}</span>
                    <span className="font-mono">{fmtDate(c.created_at)}</span>
                  </div>
                  <div className="text-sm text-zinc-800">{c.body}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!extendPort} onOpenChange={(o) => !o && setExtendPort(null)}>
        <DialogContent data-testid="extend-portfolio-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Extend Subscription</DialogTitle>
          </DialogHeader>
          {extendPort && (
            <form onSubmit={submitExtend} className="space-y-3">
              <div className="text-xs text-zinc-500">
                Portfolio: <span className="font-mono text-zinc-900">{strategies.find((s) => s.strategy_id === extendPort.strategy_id)?.name || extendPort.portfolio_id}</span>
              </div>
              <div className="text-xs text-zinc-500">
                Current end date: <span className="font-mono text-zinc-900">{fmtDate(extendPort.end_date)}</span>
              </div>
              <div>
                <Label>Duration</Label>
                <select
                  data-testid="extend-months"
                  value={extendForm.months}
                  onChange={(e) => setExtendForm({ ...extendForm, months: Number(e.target.value) })}
                  className="mt-1 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm"
                >
                  <option value={6}>6 months</option>
                  <option value={12}>1 year</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  data-testid="extend-complimentary"
                  checked={extendForm.is_complimentary}
                  onCheckedChange={(v) => setExtendForm({ ...extendForm, is_complimentary: !!v })}
                />
                <Label>Complimentary extension (no payment required)</Label>
              </div>
              <div>
                <Label>Payment reference / UTR {!extendForm.is_complimentary && <span className="text-loss">*</span>}</Label>
                <Input
                  data-testid="extend-payment-ref"
                  value={extendForm.payment_reference}
                  onChange={(e) => setExtendForm({ ...extendForm, payment_reference: e.target.value })}
                  placeholder="Unique UTR / bank reference"
                  required={!extendForm.is_complimentary}
                  className="mt-1 font-mono"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Must be globally unique — cannot be reused for another portfolio/extension.</p>
              </div>
              <div>
                <Label>Amount (₹)</Label>
                <Input
                  data-testid="extend-amount"
                  type="number"
                  value={extendForm.amount}
                  onChange={(e) => setExtendForm({ ...extendForm, amount: e.target.value })}
                  placeholder="Optional"
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  data-testid="extend-notes"
                  value={extendForm.notes}
                  onChange={(e) => setExtendForm({ ...extendForm, notes: e.target.value })}
                  placeholder="Optional"
                  className="mt-1"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setExtendPort(null)}>Cancel</Button>
                <Button data-testid="extend-submit-btn" type="submit" disabled={extendBusy} className="bg-zinc-950 hover:bg-zinc-800 text-white">
                  {extendBusy ? "Extending…" : "Confirm Extension"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NotifyPanel({ client, onClientUpdate }) {
  const [tgChatId, setTgChatId] = useState(client.telegram_chat_id || "");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    stock_name: "",
    symbol: "",
    action: "BUY",
    date: new Date().toLocaleDateString("en-GB").replace(/\//g, "/"),
    notes: "",
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const saveChatId = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/clients/${client.user_id}`, { telegram_chat_id: tgChatId });
      onClientUpdate({ telegram_chat_id: tgChatId });
      toast.success("Telegram Chat ID saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendAlert = async (e) => {
    e.preventDefault();
    setSending(true);
    setResult(null);
    try {
      const r = await api.post("/send-notification", { client_id: client.user_id, ...form });
      setResult(r.data);
      toast.success("Alert dispatched");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const StatusPill = ({ label, status, reason }) => {
    const icon = status === "sent" ? "✅" : status === "failed" ? "❌" : "⚠️";
    const color =
      status === "sent" ? "text-emerald-700 bg-emerald-50 border-emerald-200" :
      status === "failed" ? "text-rose-700 bg-rose-50 border-rose-200" :
      "text-amber-700 bg-amber-50 border-amber-200";
    return (
      <div className={`px-3 py-2 rounded-md border text-sm ${color}`} data-testid={`notif-${label.toLowerCase()}-status`}>
        <span className="font-medium">{icon} {label}</span>
        <span className="ml-2">→ {status}</span>
        {reason && <div className="text-xs mt-1 opacity-80">Reason: {reason}</div>}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <Card className="border-zinc-200">
        <CardContent className="p-6">
          <h3 className="font-display font-semibold text-zinc-950">Client contact for alerts</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="text-xs font-medium text-zinc-600">Email</label>
              <div className="mt-1 font-mono text-sm text-zinc-900 py-2">{client.email || "—"}</div>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Telegram Chat ID</label>
              <div className="flex gap-2 mt-1">
                <Input
                  data-testid="telegram-chat-id-input"
                  value={tgChatId}
                  onChange={(e) => setTgChatId(e.target.value)}
                  placeholder="e.g. 123456789"
                  className="font-mono"
                />
                <Button data-testid="save-telegram-chat-id" onClick={saveChatId} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </Button>
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                Ask client to message <span className="font-mono">@alphacompounding_alert_bot</span> on Telegram and press START. Their Chat ID will be shared automatically.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-zinc-200">
        <CardContent className="p-6">
          <h3 className="font-display font-semibold text-zinc-950">Send personalized alert</h3>
          <form onSubmit={sendAlert} className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <div>
              <label className="text-xs font-medium text-zinc-600">Action</label>
              <select data-testid="notif-action" value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} className="mt-1 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm">
                <option>BUY</option><option>SELL</option><option>HOLD</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Stock Name</label>
              <Input data-testid="notif-stock-name" required value={form.stock_name} onChange={(e) => setForm({ ...form, stock_name: e.target.value })} placeholder="Infosys" className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Symbol</label>
              <Input data-testid="notif-symbol" required value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })} placeholder="INFY" className="mt-1 font-mono" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Date (DD/MM/YYYY)</label>
              <Input data-testid="notif-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 font-mono" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-zinc-600">Notes (optional)</label>
              <Input data-testid="notif-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional context for the client…" className="mt-1" />
            </div>
            <div className="md:col-span-3">
              <Button data-testid="send-notification-btn" type="submit" disabled={sending} className="bg-zinc-950 hover:bg-zinc-800 text-white">
                {sending ? "Dispatching…" : "Submit & Notify Client"}
              </Button>
            </div>
          </form>

          {result && (
            <div className="mt-6 border border-zinc-200 rounded-lg p-4 bg-zinc-50">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-3">Notification Status</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <StatusPill label="Email" status={result.email.status} reason={result.email.reason} />
                <StatusPill label="Telegram" status={result.telegram.status} reason={result.telegram.reason} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
